import { useEffect, useState } from "react";
import { AccountPresetIcon } from "@/features/accounts/AccountPresetIcon";
import type { AccountIconKey } from "@/features/accounts/accountIconOptions";
import { getAccountPhotoBlob } from "@/lib/mail/accountPhotoCache";
import { readAccountSettings } from "@/lib/mail/accountSettingsPrefs";
import type { MailAccountSummary } from "@/lib/types/mail";
import { cn } from "@/lib/cn";

type Props = {
  account: MailAccountSummary;
  size?: "sm" | "md" | "lg";
  className?: string;
  /** Aperçu dans les paramètres (avant enregistrement). */
  iconKeyOverride?: AccountIconKey;
};

const SIZE_CLASS = {
  sm: "h-7 w-7",
  md: "h-10 w-10",
  lg: "h-14 w-14",
} as const;

export function AccountAvatar({ account, size = "sm", className, iconKeyOverride }: Props) {
  const [photoBlob, setPhotoBlob] = useState<string | null>(null);
  const iconKey = iconKeyOverride ?? readAccountSettings(account.id).accountIcon;

  useEffect(() => {
    if (!account.photoUrl || (account.photoVersion ?? 0) <= 0) {
      setPhotoBlob(null);
      return;
    }

    let cancelled = false;
    void getAccountPhotoBlob(account).then((url) => {
      if (!cancelled) setPhotoBlob(url);
    });

    return () => {
      cancelled = true;
    };
  }, [account.id, account.photoUrl, account.photoVersion]);

  if (photoBlob) {
    return (
      <img
        src={photoBlob}
        alt=""
        className={cn(
          "inline-flex shrink-0 rounded-full object-cover shadow-sm ring-1 ring-[var(--mail-border-soft)]",
          SIZE_CLASS[size],
          className
        )}
      />
    );
  }

  return (
    <span
      className={cn(
        "inline-flex shrink-0 overflow-hidden rounded-full shadow-sm ring-1 ring-[var(--mail-border-soft)]",
        SIZE_CLASS[size],
        className
      )}
    >
      <AccountPresetIcon
        iconKey={iconKey}
        providerId={account.providerId}
        variant="avatar"
        className="h-full w-full"
      />
    </span>
  );
}
